import { CollectionAsset, CollectionFolder } from 'editor/src/store/collections/types';
import { MediaElement, MediaImage } from 'editor/src/store/design/types';
import { PluginName } from 'editor/src/store/plugins/types';

export enum CollectionDisplayOption {
  Thumbnail,
  Dropdown,
  Color,
}

export interface AdvancedPersonalisationElementData {
  folderId: string;
  displayOption: CollectionDisplayOption;
  // Ids of the assets this element offers. When set, the element shows only this subset of the
  // folder pool (per-page curation); when absent, it shows the whole folder (legacy behaviour).
  assetIds?: string[];
  // Shared id across linked collection layers; one customer choice drives all of them by index.
  linkGroupId?: string;
  // Dropdown label overrides keyed by assetId; fallback is the asset name.
  assetLabels?: { [assetId: string]: string };
}

export function getCollectionAssetLabel(
  collectionInfo: AdvancedPersonalisationElementData,
  asset: CollectionAsset,
): string {
  return collectionInfo.assetLabels?.[asset.id] || asset.name;
}

export function getAdvPersoDataFromElement(element: MediaElement): AdvancedPersonalisationElementData | undefined {
  return (
    element.type === 'image' &&
    (element.plugins?.[PluginName.AdvancedPersonalisation] || element.plugins?.[PluginName.personalizationStudio])
  );
}

// Rebuilds the full plugins map with the new advanced-personalisation data written under both
// legacy and current plugin keys (same dual-key write as setAdvPersoDataOnElement). Never
// mutates — returns a fresh object for use in an elementUpdate passed to updateMediaElementAction.
export function buildAdvPersoPluginsUpdate(element: MediaImage, data: AdvancedPersonalisationElementData) {
  return {
    ...element.plugins,
    [PluginName.AdvancedPersonalisation]: data,
    [PluginName.personalizationStudio]: data,
  };
}

export function setAdvPersoDataOnElement(
  element: Partial<MediaImage>,
  folderId: string,
  displayOption: CollectionDisplayOption,
  assetIds?: string[],
) {
  const plugins = element.plugins ? { ...element.plugins } : {};
  const data: AdvancedPersonalisationElementData = { folderId, displayOption };
  if (assetIds?.length) {
    data.assetIds = assetIds;
  }
  // TODO remove advancedPersonalizationPlugin after cleanup from apps n services.
  plugins[PluginName.AdvancedPersonalisation] = data;
  plugins[PluginName.personalizationStudio] = data;
  element.plugins = plugins;
}

// Assets an element offers: the element's curated subset (advPersoData.assetIds) resolved against
// the full folder pool, preserving folder order. Falls back to the whole folder when no subset is
// stored, so legacy elements keep showing every asset.
export function getElementCollectionAssets(
  folder: CollectionFolder,
  collectionInfo: AdvancedPersonalisationElementData,
): CollectionAsset[] {
  const { assetIds } = collectionInfo;
  if (!assetIds?.length) {
    return folder.assets;
  }
  const idSet = new Set(assetIds);
  return folder.assets.filter((asset) => idSet.has(asset.id));
}
